import React, { useMemo, useRef, useState } from 'react';
import { useHistory, useIntl } from 'umi';
import { isEmpty, orderBy } from 'lodash-es';
import HomeUseCaseIcon from '../../../svg/agent-home-use-case.svg';
import HomeUseCaseRightIcon from '../../../svg/arrow-narrow-up-right.svg';
import HomeUseCaseVideoPlayIcon from '../../../svg/agent-video-play.svg';
import { v4 as uuidv4 } from 'uuid';
import { useRequestController } from '../../hooks/useRequestController.ts';
import { parseJsonSafely } from '../../../ChatPage/utils';
import VideoPreview from '../VideoPreview';
import { getName } from '@home/utils/common';
import './index.scss';
import Abnormal from '@home/components/Abnormal';
import EllipsisHover from '@gp/components/EllipsisHover';
import { TEST_USE_CASE_DATA } from '../../../config/testRainbow';


interface IVideoData {
  visible: boolean;
  src: string;
  poster: string;
  name: string;
}

const UseCase: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const player = useRef(null);
  const history: any = useHistory();
  const [currentVideo, setCurrentVideo] = useState<IVideoData>({
    visible: false,
    src: '',
    poster: '',
    name: '',
  });
  const isEN = locale === 'en-US';
  const { data, loading, mutate } = useRequestController({
    method: 'POST',
    url: `/api/v1/permission/rainbow`,
    refreshDeps: useMemo(() => ({ key: '', group: 'agent.usecase' }), []),
    // 默认30分钟不发起请求
    staleTime: 1800000,
    cacheKey: 'home-use-case',
    onError: () => {
      mutate?.(TEST_USE_CASE_DATA);
    }
  });
  const realData = useMemo(()=>{
    const dataSource = data?.rows?.map((row) => {
      const parsedValue = parseJsonSafely(row.value);
      return {
        ...row,
        ...parsedValue || {},
      };
    });
    const sortData = orderBy(dataSource, ['sort'], ['asc']);
    return sortData;
  }, [data?.rows]);

  // 处理点击视频播放的逻辑
  const handleOpenVideoPreview = (item: any) => {
    setCurrentVideo({
      visible: true,
      src: item?.videoUrl || '',
      poster: item?.cover || '',
      name: isEN ? item?.nameEn : item?.nameCn || '',
    });
  };
  // 控制视频预览的显示隐藏 
  const handleCloseVideoPreview = () => {
    setCurrentVideo({
      ...currentVideo,
      visible: false,
    });
  };

  // 页面跳转
  const handleClickQuestion = (task) => {
    const sessionId = uuidv4();
    const messageId = uuidv4();
    const question = getName(isEN, task?.questionEn, task?.questionCn);
    if (!question) return;
    history.push({
      pathname: '/agent/chat',
      search: `?sessionId=${sessionId}`,
      state: {
        initialMessage: question,
        messageId,
        from: 'homeUseCase'
      },
    });
  };

  return (
    <div className="dba-use-case-container">
      <div className="dba-use-case-header">
        <img src={HomeUseCaseIcon} alt="" />
        <span className="dba-use-case-title">{formatMessage({ id: 'dba_use_case_title' })}</span>
      </div>
      <Abnormal
        isLoading={loading}
        isNoData={isEmpty(realData) && isEmpty(data)}
        isError={false}
        component={
          <div className="dba-use-case-grid">
            {realData?.map((item) => (
              <div key={item.key} className="dba-use-case-card">
                <div className="dba-use-case-play-icon">
                  <img src={HomeUseCaseVideoPlayIcon} className="dba-use-case-play-icon-center" alt="" onClick={() => handleOpenVideoPreview(item)}/>
                  <img src={item?.cover} className="dba-use-case-play-icon-cover" alt=""/>
                </div>
                <div className="dba-use-case-content">
                  <div className="dba-use-case-card-title">
                    <EllipsisHover
                      style={{
                        width: '255px',
                      }}
                      text= {isEN ? item?.nameEn : item?.nameCn}
                    />
                  </div>
                  <div
                    className="dba-use-case-btn"
                    onClick={() => handleClickQuestion(item)}
                  >
                    {formatMessage({ id: 'dba_use_case_view_chat' })}
                    <img src={HomeUseCaseRightIcon} alt="" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        }
      />
      <VideoPreview
        visible={currentVideo.visible}
        title={currentVideo.name}
        onClose={handleCloseVideoPreview}
      >
        <video
          ref={player}
          autoPlay
          controls
          src={currentVideo.src}
          poster={currentVideo.poster}
        />
      </VideoPreview>
    </div>
  );
};

export default UseCase;